import { useMemo } from "react";
import type { InfiniteData } from "react-query";
import type { ComboBoxInputItemType } from "@ui-kit/form/ComboBox/ComboBox.props";
import type { SearchServiceConfig } from "@shared/components/application/form/FormField/types";
import type { PaginatorContract } from "@shared/services/api/types";
import { getFlattenedData } from "@shared/utils/data/data-utils";
import { formatDate } from "@shared/utils/dates/formatting";
import useVenueEventsService from "../../../features/events/data/services/events/useVenueEventsService";
import { VenueBasedEventSerializer } from "../../../features/events/data/types/events";

type EventItem = ComboBoxInputItemType & VenueBasedEventSerializer;

export default function useSearchEvent({
  eventSearchInput,
  enabled = true,
  customParams,
  single = false,
  service: serviceOverride,
  showPastEvents,
}: {
  eventSearchInput: string;
  enabled?: boolean;
  customParams?: Record<string, unknown>;
  single?: boolean;
  /** When provided, use this service instead of useVenueEventsService() */
  service?: SearchServiceConfig;
  /** When defined, filters events by past/future based on ends_on date */
  showPastEvents?: boolean;
}): {
  items: EventItem[] | ComboBoxInputItemType[];
  isFetching: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
} {
  const defaultService = useVenueEventsService();
  const resolvedService = serviceOverride ?? defaultService;
  const { usePaginated } = resolvedService;

  const customParamsKey = JSON.stringify(customParams ?? {});

  const computedParams = useMemo(() => {
    const baseParams: Record<string, unknown> = JSON.parse(customParamsKey);

    if (showPastEvents !== undefined) {
      const now = new Date().toISOString();

      if (showPastEvents) {
        baseParams.ends_on__lt = now;
        baseParams.ordering = "-starts_on";
      } else {
        baseParams.ends_on__gte = now;
        baseParams.ordering = "starts_on";
      }
    }

    return baseParams;
  }, [customParamsKey, showPastEvents]);

  const {
    data: eventSearchData,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePaginated({
    params: {
      q: eventSearchInput,
      page_size: 5,
      only_parents: false,
      ...computedParams,
    },
    options: { enabled },
  });

  const flattenedData = eventSearchData
    ? getFlattenedData(
        eventSearchData as InfiniteData<
          PaginatorContract<VenueBasedEventSerializer>
        >
      )
    : [];

  const singleSelectItems: EventItem[] = flattenedData as EventItem[];

  const multiSelectItems: ComboBoxInputItemType[] =
    eventSearchData && eventSearchData.pages
      ? (flattenedData as VenueBasedEventSerializer[]).map((item) => {
          const dateFormatString = "MMM D, YYYY h:mm A";
          const formattedStartsOnDate = formatDate({
            date: item.starts_on,
            timezone: item.timezone ?? null,
            format: dateFormatString,
          });

          const formattedEndsOnDate =
            item.ends_on &&
            formatDate({
              date: item.ends_on,
              timezone: item.timezone ?? null,
              format: dateFormatString,
            });
          return {
            ...item,
            id: item.id,
            name: `${item.name}`,
            displayName: item.name,
            starts_on: item.starts_on,
            ends_on: item.ends_on,
            timezone: item.timezone,
            date: `${formattedStartsOnDate} - ${formattedEndsOnDate}`,
          };
        })
      : ([] as ComboBoxInputItemType[]);

  return {
    items: single ? singleSelectItems : multiSelectItems,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}
